import {useCallback, useEffect} from 'preact/hooks';
import Sentry from '../lib/sentry.js';

export function useSentryErrorTracking() {
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            Sentry.addBreadcrumb({
                message: `Unhandled rejection: ${event.reason?.message || String(event.reason)}`,
                category: 'error',
                level: 'error',
            });
            Sentry.captureException(event.reason || new Error('Unhandled Promise Rejection'));
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    }, []);

    const withTracking = useCallback((asyncFn, name = 'Operation', category = 'operation') => {
        return async (...args) => {
            const start = performance.now();
            Sentry.addBreadcrumb({message: `Starting ${category}: ${name}`, category, level: 'info'});

            try {
                const result = await asyncFn(...args);
                const duration = performance.now() - start;
                Sentry.addBreadcrumb({
                    message: `Completed ${category}: ${name} (${Math.round(duration)}ms)`,
                    category,
                    level: 'info',
                });
                return result;
            } catch (error) {
                const duration = performance.now() - start;
                Sentry.addBreadcrumb({
                    message: `Failed ${category}: ${name} after ${Math.round(duration)}ms`,
                    category,
                    level: 'error',
                });
                Sentry.captureException(error, {contexts: {[category]: {name, duration}}});
                throw error;
            }
        };
    }, []);

    const withErrorTracking = useCallback((asyncFn, name = 'Async Operation') => {
        return withTracking(asyncFn, name, 'operation');
    }, [withTracking]);

    const withFetchTracking = useCallback((asyncFn, name = 'Fetch') => {
        return withTracking(asyncFn, name, 'http');
    }, [withTracking]);

    return {withErrorTracking, withFetchTracking};
}

export default useSentryErrorTracking;
